import { useCallback, useEffect, useRef, useState }  from 'react';
import { observer } from "mobx-react-lite";
import { autorun } from "mobx";
import { Button } from "@material-ui/core";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {Toast, ToastProps} from "../components/Toast";
import { DroppableImageBox } from "./DroppableImageBox";
import { VoteRoundHeading } from "./VoteRoundHeading";
import { StateService } from "../services/StateService";
import { RankedUsers } from "../components/RankedUsers";
import { ImageGallery } from "./ImageGallery";

import "./RankingOverview.scss";
import { InfoBox } from './InfoBox';

interface RankingOverviewProps {
  state: StateService;
}

// these are the round parameters that probably won't chenge (i.e. not end_time or active).
interface InvariantRoundEntry {
  name: string;
  vote: boolean;
  n_votes?: number;
  n_winners?: number;
  title?: string;
}

// Function that returns whether you have submitted a vote
function haveSubmittedSelf(state: StateService, selected_round: string): boolean {
  return state.userVotes.userVoted(selected_round);
}

export const RankingOverview = observer((props: RankingOverviewProps) => {
  const [activeRound, setActiveRound] = useState<InvariantRoundEntry | null>(null);
  const messageToast = useRef<ToastProps>(null);
  const currentTime = Date.now();
  const selectedImages = [];
  // props for showing an image on the screen
  const [open, setOpen] = useState(false);
  const [image, setImage] = useState("");

  const openImage = (e: any, img: string) => {
    e.preventDefault();
    setImage(img);
    setOpen(true);
  };

  useEffect(() => {
    let disposer = autorun(reaction => {
      const round = props.state.rounds.getActiveVoteRoundOrLatest();
      if (activeRound === null && round !== undefined) {
        setActiveRound(round);
        props.state.syncUserVotes();
        reaction.dispose();
      }
      props.state.syncImages();
    });
    return () => {
      disposer();
    };
  }, [
    props.state,
    props.state.rounds,
    props.state.authentication,
    activeRound,
  ]);

  const selectRound = (e: any, round: InvariantRoundEntry) => {
    e.preventDefault();
    props.state.syncUserVotes();
    setActiveRound(round);
  };

  const vote = useCallback(
    async (event: any) => {
      event.preventDefault();

      const selection = props.state.images.getSelection(
        activeRound?.name ?? ""
      );

      if (selection.length === 0) {
        messageToast.current?.showMessage("You must select images before voting!", 'error');
        return;
      }

      if (selection.length !== 5) {
        messageToast.current?.showMessage("You must select five images before voting!", 'error');
        return;
      }

      const result = await props.state.api.submitVotes(
        activeRound?.name ?? "",
        selection.map((v) => v.image_id)
      );
      if (result.statusCode !== 200) {
        messageToast.current?.showMessage("Failed to submit vote!", 'error');
        props.state.syncImages();
      } else {
        props.state.syncUserVotes();
        props.state.syncImages();
      }
    },
    [props.state, activeRound]
  );

  // Fill up with empty initially
  for (let i = selectedImages.length; i < 5; ++i) {
    selectedImages.push(
      <DroppableImageBox
        key={i}
        hasImage={false}
        rank={i + 1}
        thumbnail="none"
        src="none"
        state={props.state}
        openImage={openImage}
        active={props.state.rounds.getActiveRound() === activeRound?.name}
        disabled={haveSubmittedSelf(props.state, activeRound?.name ?? "")}
      />
    );
  }

  // Only show images if user authed
  if(props.state.authentication.getAuthStatus()) {
    props.state.images.getSelection(activeRound?.name ?? "").forEach((v, i) => {
      selectedImages[v.selectionRank - 1] = (
        <DroppableImageBox
          key={v.selectionRank - 1}
          hasImage={true}
          rank={v.selectionRank}
          thumbnail={v.thumbnail}
          src={v.src}
          caption={v.thumbnailCaption}
          state={props.state}
          openImage={openImage}
          active={props.state.rounds.getActiveRound() === activeRound?.name}
          disabled={haveSubmittedSelf(props.state, activeRound?.name ?? "")}
        />
      );
    });
  }

  // need to get this from rounds rather than activeRound.end_time which is not always updated
  const endTime = props.state.rounds.getPhaseEndTime(activeRound?.name || '')?.getTime() ?? Date.now();

  return (
    <div className="voting-sections">
      <div className="how-voting-works">
        <div className="gradient-right"></div>
        <div className="container">
          <div className="row align-items-center">
            {props.state.rounds.getVotingRounds().map((v, i) => {
              let allVotesPresent = props.state.userVotes.allUsersVoted(v.name);
              return (
                <VoteRoundHeading
                  index={i}
                  round={v}
                  selectRound={selectRound}
                  activeRound={activeRound}
                  allVotesPresent={allVotesPresent}
                  key={i}
                />
              );
            })}
          </div>
        </div>
      </div>

      <DndProvider backend={HTML5Backend}>
        <div className="place-your-vote">
          {currentTime < endTime && props.state.rounds.getActiveRound() === activeRound?.name && (
          <div className="container">
            <div className="row">
              <div className="col-md-6 margin-bttom pyv-title">
                <h2
                  className={
                    props.state.userVotes.userVoted(activeRound?.name)
                        ? "secTitleAlt ms-2"
                        : "secTitle ms-2"
                    }
                >

                  {!props.state.userVotes.userVoted(activeRound?.name)
                      ? "Place Your Vote"
                      : "Your Submitted Images"
                  }

                </h2>

                {!props.state.userVotes.userVoted(activeRound?.name) && (
                  <Button
                    className="purpleBtn"
                    onClick={vote}
                    disabled={!props.state.images.allImageSelected(activeRound?.name ?? "")
                    || !props.state.authentication.getAuthStatus()
                    || currentTime > endTime}
                  >
                    Vote Now
                  </Button>
                )}
              </div>
            </div>

            <Toast ref={messageToast} />
            <div className="row">{selectedImages}</div>
          </div>
           )}
           <div className="rightBottomLayout">
            <div className="BottominnerLay"></div>
            <div className="BottominnerLay1">
            </div>
          </div>
        </div>

        <div className="ranking-section">
          <div className="container">
            <div className="row">
              <div className="col-12 col-lg-9 col-md-8">
                <div className="row">
                  <div className="col-md-6 margin-bttom">
                    <div className="winning-info">
                      <h2 className="secTitle winningTitle ms-2">AI Artworks</h2>
                        {!(props.state.rounds.getActiveRound() === activeRound?.name && currentTime < endTime) ?
                            <InfoBox
                                title="These images were made by the AI art model, based on the entropy and the votes.
                            Winning images are marked with a trophy. The images you voted for are marked with a star."
                            /> : <InfoBox
                                title="These images were made by the AI art model, based on the entropy and the votes.
                          Vote now and shape the AI's output."
                            />
                        }
                    </div>
                  </div>
                </div>

                {(props.state.rounds.getActiveRound() === activeRound?.name ||
                    currentTime >= endTime) ? (
                    <div className="row">
                      {props.state.images.hasImages(activeRound?.name ?? "") ? (
                        <ImageGallery
                          state={props.state}
                          round={activeRound?.name ?? "voting3"}
                          images={props.state.images.getImages(
                            activeRound?.name ?? ""
                          )}
                          active={props.state.rounds.getActiveRound() === activeRound?.name && currentTime < endTime}
                          disabled={haveSubmittedSelf(props.state, activeRound?.name ?? "")
                          || !props.state.authentication.getAuthStatus() }
                          openImage={openImage}
                          closeImage={() => setOpen(false)}
                          imageIsOpen={open}
                          image={image}
                          submittedImages={props.state.images.getSelection(activeRound?.name ?? "").map(v => v.image_id)}
                        />
                      ) : (
                        <p style={{ color: "white", fontSize: "24px" }}>
                          The AI has not generated art for this phase yet.
                        </p>
                      )}
                    </div>
                  ) : (
                        <p style={{ color: "white", fontSize: "24px" }}>
                          The AI has not generated art for this phase yet.
                        </p>
                      )}
              </div>
              {(props.state.rounds.getActiveRound() === activeRound?.name ||
                currentTime >= endTime) && <RankedUsers state={props.state} selected_round={activeRound?.name ?? ""} />}
            </div>
          </div>
        </div>
      </DndProvider>
    </div>
  );
});
